import { useEffect, useRef, useState } from 'react';
import { UserCog, LogOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useViewMode } from '../../context/ViewModeContext';

const PIN_LENGTH = 4;

export interface PINDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PINDialog({ open, onOpenChange }: PINDialogProps): JSX.Element {
  const { isElectrician, tryUnlock, resetToCustomer } = useViewMode();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [remember, setRemember] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const submit = (): void => {
    if (pin.length !== PIN_LENGTH) {
      setError('Bitte 4-stelligen PIN eingeben.');
      return;
    }
    const ok = tryUnlock(pin, remember);
    if (ok) {
      onOpenChange(false);
    } else {
      setError('Falscher PIN.');
      setPin('');
      inputRef.current?.focus();
    }
  };

  const handleReset = (): void => {
    resetToCustomer();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-[var(--rsc-primary)]" aria-hidden="true" />
            {isElectrician ? 'Elektriker-Modus' : 'Elektriker-Anmeldung'}
          </DialogTitle>
          <DialogDescription>
            {isElectrician
              ? 'Du bist im Elektriker-Modus. Alle Details, Geräte-IDs und Debug-Informationen sind sichtbar.'
              : 'PIN eingeben, um in den Elektriker-Modus zu wechseln.'}
          </DialogDescription>
        </DialogHeader>

        {!isElectrician && (
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="rsc-pin-input">PIN</Label>
              <Input
                id="rsc-pin-input"
                ref={inputRef}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={PIN_LENGTH}
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
                  setPin(digits);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder="••••"
                data-testid="pin-input"
                className="text-center tracking-[0.5em] text-lg"
              />
              {error && (
                <p role="alert" className="text-xs text-rose-500" data-testid="pin-error">
                  {error}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-input"
                data-testid="pin-remember"
              />
              Merken (bis abgemeldet)
            </label>
          </div>
        )}

        <DialogFooter>
          {isElectrician ? (
            <Button
              variant="outline"
              onClick={handleReset}
              data-testid="pin-back-to-customer"
            >
              <LogOut className="mr-1 h-4 w-4" aria-hidden="true" />
              Zurück zu Kunden-View
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={submit}
                disabled={pin.length !== PIN_LENGTH}
                data-testid="pin-submit"
              >
                Anmelden
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
